import { t, Trans } from '@lingui/macro';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import { ArrowDown, MessagesSquare } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Highlight, InstantSearch, useSearchBox, useInfiniteHits } from 'react-instantsearch-hooks-web';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import MessageFileItem from '../components/MessageFileItem';
import ProfileImg from '../components/ProfileImg';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

type RoomSearchResultsProps = {
    room: Room;
    searchQuery: string;
};

// SearchBox를 대체하는 컴포넌트 - 외부 searchQuery를 Algolia와 동기화
const SearchQuerySync: React.FC<{ query: string }> = ({ query }) => {
    const { refine } = useSearchBox();

    useEffect(() => {
        refine(query);
    }, [query, refine]);

    return null;
};

const RoomSearchResults: React.FC<RoomSearchResultsProps> = ({
    room,
    searchQuery
}) => {
    const [searchClient, setSearchClient] = useState<SearchClient | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestIdRef = useRef(0);

    // Algolia 초기화 - 검색 모드일 때만 실행
    useEffect(() => {
        if (!room?.id) {
            setError(t`Search is not available in this room right now.`);
            return;
        }

        const currentRequestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        Fire.getSearchSecuredAPIKey(room.id)
            .then((key) => {
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                setSearchClient(algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, key));
            })
            .catch((err) => {
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                console.error('Failed to fetch search secured key', err);
                setError(t`An error occurred while preparing search.`);
            })
            .finally(() => {
                if (currentRequestId === requestIdRef.current) {
                    setIsLoading(false);
                }
            });

        // Cleanup
        return () => {
            requestIdRef.current += 1;
            setSearchClient(null);
        };
    }, [room?.id]);

    if (isLoading) {
        return (
            <div className="flex-1 min-h-0 bg-white">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-1 min-h-0 bg-white">
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!searchClient) {
        return null;
    }

    return (
        <div className="flex-1 min-h-0 bg-white">
            <div className="h-full overflow-y-auto">
                <InstantSearch
                    key={`${room?.id}-${searchQuery}`}
                    indexName="messages"
                    searchClient={searchClient}
                >
                    <SearchQuerySync query={searchQuery} />
                    <div className="p-4">
                        <CustomResults />
                    </div>
                </InstantSearch>
            </div>
        </div>
    );
};

// Custom Results Component with styled Show More button (same as SearchPage)
function CustomResults() {
    const { hits, isLastPage, showMore } = useInfiniteHits();

    if (hits.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <p><Trans>No results found.</Trans></p>
            </div>
        );
    }

    return (
        <div className="space-y-4 pb-10">
            {hits.map((hit: any) => (
                <Hit key={hit.objectID} hit={hit} />
            ))}

            {!isLastPage && (
                <div className="flex justify-center pt-4">
                    <Button variant="outline" onClick={() => showMore()}>
                        <Trans>Show more results</Trans>
                    </Button>
                </div>
            )}
        </div>
    );
}
function Hit({ hit }: { hit: any }) {
    const [expendable, setExpendable] = useState(false);
    const divRef = React.useRef<HTMLDivElement>(null);
    const { setMessageToShow } = useAppUIContext();
    const { toast } = useToast();

    useEffect(() => {
        const divHeight = divRef.current?.offsetHeight ?? 0;
        setExpendable(!!hit.text && !hit.text.includes('src=') && divHeight > 300);
    }, [hit.text]);

    if (hit._highlightResult?.text?.value)
        hit._highlightResult.text.value = Util.getTextAsPlain(hit._highlightResult.text.value);

    if (!hit.text) {
        return null;
    }

    return (
        <div className='flex flex-col gap-2 p-3 rounded-lg border bg-card hover:bg-slate-50 transition-colors'>
            <div className='flex items-start gap-3'>
                <div className="shrink-0">
                    <ProfileImg url={hit['user.photoUrl']} className="w-10 h-10 rounded-full" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-col">
                            <span className="font-semibold text-sm">
                                <Highlight attribute={['user.name']} hit={hit} />
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {UtilApp.dateStrToTimeAndDay(hit.created, My.user.lang)}
                            </span>
                        </div>
                        <Button
                            className="w-8 h-8 mt-1"
                            variant="icon-box"
                            size="icon"
                            onClick={() => showMessage(hit.id)}
                            title={t`Show in chat`}
                        >
                            <MessagesSquare />
                        </Button>
                    </div>
                </div>
            </div>

            <div className='pl-[52px]'>
                {hit.text.includes('src=') ?
                    <div className="prose prose-sm max-w-none text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(hit.text, My.user.id) }} />
                    :
                    <div className={`relative text-sm whitespace-pre-wrap break-words ${expendable ? 'max-h-[300px] overflow-hidden' : ''}`} ref={divRef}>
                        <Highlight attribute='text' hit={hit} />
                        {expendable && (
                            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent flex items-end justify-center pb-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="h-8 rounded-full shadow-sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setExpendable(false);
                                    }}
                                >
                                    <ArrowDown className="h-4 w-4 mr-1" />
                                    <Trans>Read more</Trans>
                                </Button>
                            </div>
                        )}
                    </div>
                }
                {hit.files && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {hit.files.sort((a: any, b: any) => a.filename < b.filename ? -1 : 1).map((file: any, i: number) => (
                            <MessageFileItem file={file} key={i} files={hit.files} showTitle={false} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    function showMessage(id: string) {
        Fire.msg.getMessage(id).then(message => {
            if (message) {
                setMessageToShow(message);
            } else {
                toast({
                    description: t`Message could not be found. It may have been deleted.`,
                    variant: "destructive"
                });
            }
        });
    }
}

export default RoomSearchResults;
